import { Link } from "react-router";

import { AlertBell } from "@/components/shell/AlertBell";
import { MoonIcon, SearchIcon, SettingsIcon, SunIcon } from "@/components/ui/icons";
import { useThemePreference } from "@/lib/use-theme-preference";

/**
 * DESIGN.md § Components `app-shell`, phone half: "search and bell in a
 * slim top bar". Search is real (Story 9.2) — a plain `onClick` opening the
 * overlay (`AppShell.tsx`'s own `onOpenSearch`, the same one path
 * `NavRail.tsx`'s desktop trigger and the global `/`/⌘K shortcut share); the
 * bell is real (Story 3.9's `AlertBell`, shared with the desktop rail). The
 * destination name itself renders as the page's own `{typography.title}`
 * heading below, so this bar stays a wordmark plus the two global actions
 * rather than repeating it.
 *
 * Story 20.1 adds a theme toggle and a Settings shortcut to this same
 * utility cluster — reusing `useThemePreference()` (`lib/use-theme-
 * preference.ts`, shared with `Settings.tsx`'s own toggle and
 * `GlobalTopBar`'s desktop equivalent) and `SettingsIcon`, since hiding
 * Settings from the phone Menu sheet (`destinations.ts`'s `hidden: true`)
 * without adding it anywhere else on phone would leave phone users with no
 * way to reach it at all.
 */
export function PhoneTopBar({ onOpenSearch }: { onOpenSearch: () => void }) {
  const { isDark, toggle: toggleTheme } = useThemePreference();

  return (
    <header
      data-shell-scope="phone-top-bar"
      className="flex min-h-tap-target-min items-center justify-between gap-2 border-b border-border-hairline bg-surface px-4 md:hidden"
    >
      {/* Story 19.1: `text-heading` here is a size reuse for the brand
          wordmark, not a real heading — `font-sans` opts it back out of the
          `.text-heading` role's new Fraunces rule in index.css. */}
      <span className="text-heading font-sans font-semibold text-ink">PropDesk</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Search"
          onClick={onOpenSearch}
          className="flex h-tap-target-min w-tap-target-min items-center justify-center rounded-md text-ink-secondary hover:bg-surface-hover"
        >
          <SearchIcon />
        </button>
        <button
          type="button"
          onClick={() => void toggleTheme()}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          className="flex h-tap-target-min w-tap-target-min items-center justify-center rounded-md text-ink-secondary hover:bg-surface-hover"
        >
          {isDark ? <MoonIcon /> : <SunIcon />}
        </button>
        <Link
          to="/settings"
          aria-label="Settings"
          className="flex h-tap-target-min w-tap-target-min items-center justify-center rounded-md text-ink-secondary hover:bg-surface-hover"
        >
          <SettingsIcon />
        </Link>
        <AlertBell />
      </div>
    </header>
  );
}
